import { PlusCircleOutlined } from "@ant-design/icons";
import type { BundleItem } from "@p0security/shared/types/bundle";
import { Button, Flex } from "@p0security/ui";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { bundleItemLabel } from "./BundleItemDisplay";
import { GroupedSection } from "./GroupedSection";
import { UngroupedEditor } from "./UngroupedEditor";
import { MAX_BUNDLE_ITEMS, bundleItemKey } from "./useAccessBundles";

export type EditorEntry = {
  key: string;
  integrationKey?: string;
  item?: BundleItem;
};

export const BundleMemberPicker: React.FC<{
  items: BundleItem[];
  onChange: (items: BundleItem[]) => void;
}> = ({ items, onChange }) => {
  const nextKeyRef = useRef(0);

  const [entries, setEntries] = useState<EditorEntry[]>(() =>
    items.map((item) => ({
      key: `item-${nextKeyRef.current++}`,
      integrationKey: item.type,
      item,
    }))
  );

  const lastPropagatedRef = useRef(items);

  useEffect(() => {
    if (!isEqual(items, lastPropagatedRef.current)) {
      lastPropagatedRef.current = items;
      setEntries(
        items.map((item) => ({
          key: `item-${nextKeyRef.current++}`,
          integrationKey: item.type,
          item,
        }))
      );
    }
  }, [items]);

  const [availableIntegrations, setAvailableIntegrations] =
    useState<string[]>();

  const handleAvailableIntegrations = useCallback((keys: string[]) => {
    setAvailableIntegrations((prev) => prev ?? keys);
  }, []);

  // --- Derived values ---

  const groupedEntries = useMemo(() => {
    const groups = new Map<string, EditorEntry[]>();
    for (const entry of entries) {
      if (!entry.integrationKey) continue;
      const list = groups.get(entry.integrationKey) ?? [];
      list.push(entry);
      groups.set(entry.integrationKey, list);
    }
    return groups;
  }, [entries]);

  const ungroupedEntries = useMemo(
    () => entries.filter((e) => !e.integrationKey),
    [entries]
  );

  const allIntegrationKeys = useMemo(
    () => Array.from(groupedEntries.keys()),
    [groupedEntries]
  );

  const addDisabled = entries.length >= MAX_BUNDLE_ITEMS;

  const usedAvailableCount = useMemo(() => {
    if (!availableIntegrations) return 0;
    const availableSet = new Set(availableIntegrations);
    return allIntegrationKeys.filter((k) => availableSet.has(k)).length;
  }, [allIntegrationKeys, availableIntegrations]);

  const allIntegrationsUsed =
    availableIntegrations != null &&
    usedAvailableCount + ungroupedEntries.length >=
      availableIntegrations.length;

  const duplicateWarnings = useMemo(() => {
    const seen = new Map<string, string>();
    const warnings = new Map<string, string>();
    for (const entry of entries) {
      if (!entry.item) continue;
      const itemKey = bundleItemKey(entry.item);
      if (seen.has(itemKey)) {
        warnings.set(
          entry.key,
          `"${bundleItemLabel(entry.item)}" is already in this bundle.`
        );
      } else {
        seen.set(itemKey, entry.key);
      }
    }
    return warnings;
  }, [entries]);

  // --- Propagate saved items to parent ---

  const savedItems = useMemo(
    () =>
      entries
        .filter((e) => e.item && !duplicateWarnings.has(e.key))
        .map((e) => e.item!),
    [entries, duplicateWarnings]
  );

  useEffect(() => {
    if (!isEqual(savedItems, lastPropagatedRef.current)) {
      lastPropagatedRef.current = savedItems;
      onChange(savedItems);
    }
  }, [savedItems, onChange]);

  // --- Callbacks ---

  const handleEntryChange = useCallback(
    (entryKey: string, newItem: BundleItem | undefined) => {
      setEntries((prev) =>
        prev.map((e) => (e.key === entryKey ? { ...e, item: newItem } : e))
      );
    },
    []
  );

  const handleEntryRemove = useCallback((entryKey: string) => {
    setEntries((prev) => prev.filter((e) => e.key !== entryKey));
  }, []);

  const handleIntegrationSelect = useCallback(
    (entryKey: string, integration: string) => {
      setEntries((prev) => {
        const alreadyHasGroup = prev.some(
          (e) => e.key !== entryKey && e.integrationKey === integration
        );
        if (alreadyHasGroup) {
          return prev.filter((e) => e.key !== entryKey);
        }
        return prev.map((e) =>
          e.key === entryKey ? { ...e, integrationKey: integration } : e
        );
      });
    },
    []
  );

  const handleAddEntry = useCallback((integrationKey?: string) => {
    setEntries((prev) => [
      ...prev,
      {
        key: `entry-${nextKeyRef.current++}`,
        ...(integrationKey ? { integrationKey } : {}),
      },
    ]);
  }, []);

  const handleAddUngroupedEntry = useCallback(() => {
    handleAddEntry();
  }, [handleAddEntry]);

  return (
    <Flex vertical gap={24} align="flex-start">
      {allIntegrationKeys.map((integrationKey) => (
        <GroupedSection
          key={integrationKey}
          integrationKey={integrationKey}
          entries={groupedEntries.get(integrationKey) ?? []}
          onEntryChange={handleEntryChange}
          onEntryRemove={handleEntryRemove}
          onAddEntry={handleAddEntry}
          duplicateWarnings={duplicateWarnings}
          addDisabled={addDisabled}
          onAvailableIntegrations={handleAvailableIntegrations}
        />
      ))}
      {ungroupedEntries.map((entry) => (
        <div key={entry.key} className="w-full">
          <UngroupedEditor
            entryKey={entry.key}
            excludeIntegrations={allIntegrationKeys}
            onEntryChange={handleEntryChange}
            onEntryRemove={handleEntryRemove}
            onIntegrationSelect={handleIntegrationSelect}
            onAvailableIntegrations={handleAvailableIntegrations}
          />
        </div>
      ))}
      {!allIntegrationsUsed && (
        <Button
          type="link"
          size="small"
          onClick={handleAddUngroupedEntry}
          icon={<PlusCircleOutlined />}
          disabled={addDisabled}
          className="px-0!"
        >
          Add to Access Bundle
        </Button>
      )}
    </Flex>
  );
};
